/**
 * Category Styles Utility
 * 
 * Pre-generates CSS custom properties for all event categories
 * to enable GPU-accelerated class-based styling instead of
 * expensive inline style calculations.
 * 
 * Performance benefits:
 * - Zero JS execution during event selection
 * - No React re-renders for style changes
 * - GPU-accelerated class swaps
 * - Browser-native CSS optimization
 */

/**
 * Helper function to convert hex to rgba
 */
const hexToRgba = (hex, alpha) => {
  if (!hex) return `rgba(255, 255, 255, ${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Generates and injects CSS custom properties for all categories
 * Called once when categories are loaded from backend
 */
export const injectCategoryStyles = (categories) => {
  if (!categories || categories.length === 0) {
    console.warn('[categoryStyles] No categories provided');
    return;
  }

  // Check if styles already exist
  const existingStyle = document.getElementById('category-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create style element
  const styleElement = document.createElement('style');
  styleElement.id = 'category-styles';
  
  // Generate CSS for each category
  let css = '/* Auto-generated category styles for performance */\n\n';
  
  categories.forEach(category => {
    const { id, activeColor, secondaryColor, lightColor } = category;
    
    if (!id) {
      console.warn('[categoryStyles] Category missing id:', category);
      return;
    }

    // Generate CSS custom properties for this category - theme background only
    css += `
.event-category-${id} {
  --event-bg-color: var(--inyc-primary);
  --event-bg-color-solid: var(--inyc-primary);
  --event-category-color: ${activeColor || 'var(--inyc-primary-button)'};
  --event-secondary-color: var(--inyc-primary-text-color-light);
  --event-light-color: var(--inyc-primary-text-color);
  --event-text-color: var(--inyc-primary-text-color);
  --event-border-color: var(--inyc-input-border-color);
}
`;
  });

  // Add default venue styles (no category)
  css += `
.event-category-none,
.event-type-venue {
  --event-bg-color: var(--inyc-primary);
  --event-bg-color-solid: var(--inyc-primary);
  --event-secondary-color: var(--inyc-primary-text-color-light);
  --event-light-color: var(--inyc-primary-text-color);
  --event-text-color: var(--inyc-primary-text-color);
  --event-border-color: var(--inyc-input-border-color);
}
`;

  // Set the CSS content and inject into head
  styleElement.textContent = css;
  document.head.appendChild(styleElement);
  
  console.log(`[categoryStyles] ✅ Injected styles for ${categories.length} categories`);
};

/**
 * Gets the appropriate CSS class name for an event based on its type and category
 */
export const getEventCategoryClass = (event) => {
  if (!event) return 'event-category-none';
  
  // Venues don't use category colors
  if (event.type !== 'event') {
    return 'event-type-venue';
  }
  
  // Events with categories
  if (event.category) {
    return `event-category-${event.category}`;
  }
  
  // Events without categories
  return 'event-category-none';
};

